/** The frontend contract — the ONLY shared vocabulary between the shell and the panels.
 *
 * Mirrors `airsec/api/protocol.py` (the wire) and `airsec/patterns/model.py` (graded Flags).
 * If those change, this file changes with them and everything downstream follows.
 */

// ---------------------------------------------------------------- wire constants
// Byte 0 of every WS frame is a lane tag (protocol.py TAG_HOT / TAG_CTRL).
export const TAG_HOT = 0x01
export const TAG_CTRL = 0x02

/** Hot-lane record: `<I f f H` — u32 id, f32 x, f32 y, u16 flags. */
export const STRIDE = 14

// flags: low 2 bits = kind, remaining bits = state (protocol.py).
export const F_KIND = 0b11
export const F_LOST = 1 << 2
export const F_PLACED = 1 << 3
export const F_ABANDONED = 1 << 4
export const F_CARRIED = 1 << 5
export const F_SELECTED = 1 << 6

export type Kind = 'person' | 'item' | 'group'

/** Index by `flags & F_KIND` (protocol.py F_PERSON=0 / F_ITEM=1 / F_GROUP=2). */
export const KINDS: readonly Kind[] = ['person', 'item', 'group']

// ---------------------------------------------------------------- entities

/**
 * One live entity. Objects are STABLE across frames — the same `Track` instance keeps its
 * identity for the lifetime of the track, and `x`/`y` are mutated in place every animation
 * frame to the interpolated position. A renderer may therefore hold the Map and read
 * `track.x` at 60fps with zero allocation and zero React involvement (STACK D21 law 1).
 */
export interface Track {
  id: number
  /** World-model gid (`"A"`, `"BAG-1"`). Learned from the control lane; may lag by one frame. */
  gid?: string
  kind: Kind
  x: number
  y: number
  flags: number
  lost: boolean
  /** Item put down but still attended (`ItemState.PLACED`). */
  placed: boolean
  abandoned: boolean
  carried: boolean
  selected: boolean
  /**
   * Cameras that OBSERVED this track — the tracker's answer, not geometric coverage
   * (the coverage polygons over-claim ~31:1; render `seen by`, never `can be seen by`,
   * from this field). Present at `zone`/`entity` altitude. Freshness: updated whenever
   * the track rides the control lane — every snapshot (connect/re-subscribe/selection),
   * track entry, and each broadcast for groups; while an entity is SELECTED the socket
   * re-snapshots every 2s so the selected target's set tracks its movement.
   */
  cameras?: string[]
  /**
   * Producer-supplied per-track attributes, verbatim off the world model
   * (`Entity.attrs`). Rides the CONTROL lane (`TAG_CTRL`) only — the 14-byte hot record
   * is fixed-shape and must never grow to carry structure.
   */
  attrs?: TrackAttrs
}

/**
 * `Track.attrs` — open by design (a producer may attach anything; the world model merges
 * it untouched), with the fields the warroom actually reads named explicitly.
 */
export interface TrackAttrs {
  /**
   * Per-camera 2D boxes in SOURCE-IMAGE pixels: `cameraId -> [x1, y1, x2, y2]`.
   *
   * Present only when the PRODUCER supplies real boxes — Rung B replays the annotated
   * pack's own boxes (`airsec/rungb/mtmc.py`), so a box drawn from this field lands on
   * the human in the frame. Absent on Rung A, where `tiles.ts::boxesForCamera` projects
   * world-model positions through K·[R|t] instead — real data, DERIVED geometry, and the
   * UI must keep saying which of the two it is showing.
   *
   * Pixels, not tile-normalised: divide by the camera's `image_width`/`image_height`
   * before rendering (the world model deals in metres; this one field is the exception,
   * because it describes an image and nothing else can).
   */
  boxes?: Record<string, [number, number, number, number]>
  /** Producer attributes the pattern engine reads (`staff`, `screened`, `child`, …). */
  [k: string]: unknown
}

export type Severity = 'note' | 'flag' | 'red-flag'

/** A graded rule-engine finding (`airsec/patterns/model.py::Flag.to_dict`). */
export interface Flag {
  pattern: string
  name: string
  severity: Severity
  t: number
  reason: string
  entities: string[]
  item?: string | null
  zone?: string | null
  /** Frame references behind the finding, de-duplicated (`Flag.frames()`). */
  frames?: string[]
  evidence?: any
  escalate?: boolean
  escalate_reason?: string
}

// ---------------------------------------------------------------- snapshot / views

/** IA.md view tiers. Also the backend's decimation knob (`world.ALTITUDES`). */
export type Altitude = 'facility' | 'zone' | 'entity' | 'frame'

/** `(x0, y0, x1, y1)` in floor metres. */
export type Viewport = [number, number, number, number]

export interface Counts {
  persons: number
  items: number
  groups: number
  unattended_items: number
  /** Entities matching the viewport BEFORE decimation. */
  total: number
  /** Entities actually sent. `returned < total` means the backend truncated. */
  returned: number
  /**
   * Identity corrections applied by the background tier (BT-4/identity's fields, handed
   * over rather than reached across the file boundary). A merge folds two gids into one;
   * a split separates them again. Rung C acceptance reads these.
   */
  id_merge?: number
  id_split?: number
  [k: string]: number | undefined
}

/** Throttled (<=10Hz) view of world structure. Never updated from the rAF loop. */
export interface WorldSnapshot {
  counts: Counts
  /** zone id -> occupancy. */
  zones: Record<string, number>
  /** Simulation/world clock, seconds. */
  t: number
  connected: boolean
  /** Socket open but no hot frame for 3s — the view is frozen, not quiet. */
  stale: boolean
  altitude: Altitude
  /** True when the backend decimated the result — the view is not showing everything. */
  truncated: boolean
  /** Hot-lane id of the selection ANCHOR (the one the dossier is about), or null. */
  selected: number | null
  /** How many entities are selected. Selection is a SET — the anchor is one member of it. */
  selectedCount: number
  /**
   * What the SERVER did with the selection — three numbers, because two conflate two very
   * different operator situations (gate-sim's shape, adopted):
   *
   *   requested > eligible  →  your selection contains tracks that have LEFT the world
   *                            (EXITED, or a group that split below two)
   *   eligible  > shown     →  the server BOUNDED you at MAX_SELECTION
   *
   * A single "N of M" makes those indistinguishable, which is the no-silent-truncation law
   * failing in a subtler way than swallowing the number outright. `dropped` names the pins
   * the server bounded away — an operator who pins 500 and gets 200 back must never be left
   * believing they are watching someone who is not in the payload.
   *
   * Undefined until the server publishes it; a client reading this must treat absence as
   * "unknown", never as "nothing was truncated".
   */
  selection?: {
    /** Size of the `selected_ids` set this client sent. */
    requested: number
    /** …of those, how many are still present in the world after decimation's own filters. */
    eligible: number
    /** …of those, how many the server actually pinned (`min(eligible, MAX_SELECTION)`). */
    shown: number
    /** The gids bounded away. Empty in the normal case; never omitted when non-empty. */
    dropped: string[]
  }
}
